import { IUtmParams } from "repoV2/interfaces/IUtmParams";
import {
    IAnalyticsInfo,
    getAnalyticsInfo,
    initAndGetEndUserSessionId,
} from "repoV2/utils/common/analytics/analyticsInfo";
import { initAndGetReferrerWithLogic } from "repoV2/utils/common/analytics/referrer";
import { getUtmParams } from "repoV2/utils/common/analytics/utmParams";
import { UPSELL_TYPES } from "repoV2/features/Listing/Listing.constants";
import { getReturnUrl } from "repoV2/utils/payment/getReturnUrl";
import { GATEWAYS_ENUM } from "../Payments.constants";
import {
    IGeneratePaymentPayload,
    IInitiateBookingPayload,
} from "../Payments.interface";
import { generateCustomerDetails } from "./generateCustomerDetails";
import { generateListingMapDetails } from "./generateListingMapDetails";
import { generateRenewSubscriptionCustomerDetails } from "./generateRenewSubscriptionCustomerDetails";

/**
 * This utility is used to generate the payload to initiate
 * booking and call the payment api
 * @param data object containing userDetails and listingUuid
 * @returns payload for the initiate payment api call
 */
export const generatePaymentPayload = (data: IGeneratePaymentPayload) => {
    const {
        userDetails = {},
        deliveryDetails = {},
        renewSubscriptionBookingDetails = {
            userDetails: {},
            isRenewSubscription: false,
        },
        listingUuid = "",
        addressDetailsPayloadHelper = {},
        listingType,
        orderBumpsDetails = {},
        sessionDetails,
        gstDetails,
        variantDetails,
        allListingDetails,
        paymentMethod,
        firstUserId = "",
        merchandiseQuantity,
        installmentDetails,
        thankyouUuid = "",
        communityUuid = "",
        dynamicLinkUuid = "",
        multipleUsersHeadCount,
    } = data;

    const { isRenewSubscription, userDetails: renewSubscriptionDetails } =
        renewSubscriptionBookingDetails;

    const orderBumpsUuids = Object.keys(orderBumpsDetails);

    // Customers data
    const customers = isRenewSubscription
        ? generateRenewSubscriptionCustomerDetails({
              renewSubscriptionDetails,
              gstDetails,
          })
        : generateCustomerDetails({
              userDetails,
              deliveryDetails,
              gstDetails,
              addressDetailsPayloadHelper,
              firstUserId,
          });

    // Payment Method
    const isPaypalOptionSelected = paymentMethod === GATEWAYS_ENUM.PAYPAL;

    // Utm data
    const utm_details: IUtmParams & { referer?: string } = getUtmParams();
    const referer = initAndGetReferrerWithLogic();
    if (referer) utm_details.referer = referer;

    // Return url
    const return_url =
        getReturnUrl({
            isDirectBooking: false,
            eventId: listingUuid,
            from: `${window.location}`, // Need to send the current page complete url as 'from' so that the
            // analytic and utm params (if any) will be maintained when user returns to this page
        }) || "";

    // Analytics info
    const analytics_info: IAnalyticsInfo = getAnalyticsInfo();

    // Session id
    const session_id: string = initAndGetEndUserSessionId();

    // Listing id - This will also include sesson data
    const listing_map = generateListingMapDetails({
        listingType,
        sessionDetails,
        allListingDetails,
        primaryListingUuid: listingUuid,
        installmentDetails,
        orderBumpsDetails,
        variantDetails,
        dynamicLinkUuid,
        multipleUsersHeadCount,
        merchandiseQuantity,
    });

    // Supported gateways, incase customer enables paypal option as a payment method,
    // then we choose Razorpay as a gateway to support paypal, else we provide all other four gateways
    const gateways_supported = isPaypalOptionSelected
        ? [GATEWAYS_ENUM.RAZORPAY]
        : [
              GATEWAYS_ENUM.STRIPE,
              GATEWAYS_ENUM.NEW_JUSPAY,
              GATEWAYS_ENUM.RAZORPAY,
              GATEWAYS_ENUM.TAZAPAY,
          ];

    const payload: IInitiateBookingPayload = {
        customers,
        utm_details,
        listing_uuid: listingUuid,
        return_url,
        analytics_info,
        session_id,
        listing_map,
        gateways_supported,
        ...(isPaypalOptionSelected && {
            is_paypal_order: isPaypalOptionSelected,
        }),
        ...(isRenewSubscription && {
            is_renewal: isRenewSubscription,
            renewal_email: renewSubscriptionDetails?.email,
        }),
        ...(thankyouUuid && {
            thankyou_page_uuid: thankyouUuid,
        }),
        ...(orderBumpsUuids.length && {
            other_listings: orderBumpsUuids,
            is_upsell_order: true,
            upsell_type: UPSELL_TYPES.ORDER_BUMPS,
        }),
        ...(communityUuid && {
            community_listing_id: communityUuid,
        }),
    };
    return payload;
};
